/** @jsxImportSource @emotion/react */
import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import type { ThemeType } from "@packages/ui/emotion";
import { Text } from "@packages/ui/emotion";

import type { Game } from "../schema";

const cssDivContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
  width: "100%",
});
const cssDivContainerStats: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.m,
});
const cssDivContainerStatsM: Interpolation = {
  width: "fit-content",
};
const cssDivContainerStatsL: Interpolation = {
  width: "100%",
};
const cssDivContainerStatisticL: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.t,
  justifyContent: "center",
  textAlign: "center",
  width: "100%",
});
const cssDivContainerStatisticM: Interpolation<ThemeType> = (theme) => ({
  alignItems: "flex-end",
  display: "flex",
  gap: theme.spacing.xxs,
  justifyContent: "center",
  textAlign: "center",
  width: "fit-content",
});
const cssTextDetail: Interpolation<ThemeType> = (theme) => ({
  color: theme.tints.tint60,
});
const cssTextDetailM: Interpolation = {
  textTransform: "lowercase",
};
const cssTextP: Interpolation<ThemeType> = (theme) => ({
  color: theme.tints.tint60,
  textAlign: "center",
});

export const Stats = ({
  isDisclaimer,
  size,
  summary,
}: {
  isDisclaimer?: boolean;
  size: "l" | "m";
  summary: Game["state"]["summary"];
}) => {
  const t = useTranslations();

  return (
    <div css={cssDivContainer}>
      <div
        css={[
          cssDivContainerStats,
          size === "l" ? cssDivContainerStatsL : cssDivContainerStatsM,
        ]}
      >
        <div
          css={
            size === "l" ? cssDivContainerStatisticL : cssDivContainerStatisticM
          }
        >
          <Text size={size === "l" ? "xl" : "l"} type="title">
            {summary.win_percentage}%
          </Text>
          <Text
            css={[cssTextDetail, size === "m" && cssTextDetailM]}
            size="l"
            type="detail"
          >
            <I18nMessage message="games_win_percentage" />
          </Text>
        </div>
        <div
          css={
            size === "l" ? cssDivContainerStatisticL : cssDivContainerStatisticM
          }
        >
          <Text size={size === "l" ? "xl" : "l"} type="title">
            {summary.streak}
          </Text>
          <Text
            css={[cssTextDetail, size === "m" && cssTextDetailM]}
            size="l"
            type="detail"
          >
            <I18nMessage
              message={size === "l" ? "games_current_streak" : "games_streak"}
            />
          </Text>
        </div>
        <div
          css={
            size === "l" ? cssDivContainerStatisticL : cssDivContainerStatisticM
          }
        >
          <Text size={size === "l" ? "xl" : "l"} type="title">
            {summary.longest_streak}
          </Text>
          <Text
            css={[cssTextDetail, size === "m" && cssTextDetailM]}
            size="l"
            type="detail"
          >
            <I18nMessage
              message={size === "l" ? "games_max_streak" : "games_max"}
            />
          </Text>
        </div>
      </div>
      {isDisclaimer && (
        <Text
          as="p"
          css={cssTextP}
          dangerouslySetInnerHTML={{ __html: t("games_replay_disclaimer") }}
          size="l"
          type="detail"
        />
      )}
    </div>
  );
};
